import { useMemo } from "react";
import AdminShell from "@/pages/admin/components/AdminShell";
import { analyzeSeo, listSeoRoutes, SITE } from "@/utils/seo";

const scoreTone = (score: number): string => {
  if (score >= 90) return "bg-accent-100 text-accent-900";
  if (score >= 70) return "bg-secondary-100 text-secondary-900";
  return "bg-primary-100 text-primary-800";
};

const barTone = (score: number): string => {
  if (score >= 90) return "bg-accent-500";
  if (score >= 70) return "bg-secondary-500";
  return "bg-primary-500";
};

export default function SeoDashboardPage() {
  const reports = useMemo(() => listSeoRoutes().map((path) => analyzeSeo(path)), []);
  const average = reports.length
    ? Math.round(reports.reduce((sum, report) => sum + report.score, 0) / reports.length)
    : 0;
  const perfect = reports.filter((report) => report.score === 100).length;

  return (
    <AdminShell
      title="SEO Report"
      subtitle="Auto-generated metadata for every route, scored against on-page best practice."
      active="seo"
    >
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Average score</p>
          <p className="mt-1 font-heading text-3xl font-bold text-foreground-950">{average}%</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Routes configured</p>
          <p className="mt-1 font-heading text-3xl font-bold text-foreground-950">{reports.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Perfect scores</p>
          <p className="mt-1 font-heading text-3xl font-bold text-foreground-950">{perfect}</p>
        </div>
      </section>

      <p className="mt-4 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-xs leading-relaxed text-foreground-600">
        Titles, descriptions, keywords, canonical URLs and Open Graph / Twitter tags are generated automatically from{" "}
        <code className="rounded bg-background-200 px-1.5 py-0.5">src/config/seo.ts</code>. Add a route there and it is emitted
        site-wide by the auto-SEO layer. Canonical base: <span className="font-medium text-foreground-800">{SITE}</span>
      </p>

      <section className="mt-6 space-y-3">
        {reports.map((report) => (
          <div key={report.path} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-xs text-foreground-500">{report.path}</p>
                <h3 className="mt-1 font-heading text-base font-semibold text-foreground-950">{report.entry.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{report.entry.description}</p>
              </div>
              <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold ${scoreTone(report.score)}`}>
                {report.score}%
              </span>
            </div>

            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
              <div className={`h-full rounded-full ${barTone(report.score)}`} style={{ width: `${report.score}%` }} />
            </div>

            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {report.checks.map((check) => (
                <li key={check.label} className="flex items-start gap-2 text-xs">
                  <i
                    className={`${check.pass ? "ri-checkbox-circle-fill text-accent-600" : "ri-error-warning-fill text-primary-600"} mt-0.5 text-base`}
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block font-semibold text-foreground-800">{check.label}</span>
                    <span className="block text-foreground-500">{check.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </AdminShell>
  );
}